import type { Strings } from "../i18n/strings";
import { ARCHETYPE_NAME, locDate } from "../i18n/strings";
import { archetypeColor } from "../lib/archetypeMeta";
import { useCountUp } from "../lib/useMotion";
import { deriveDisplayStats } from "../lib/displayStats";
import { BUCKET_COUNT } from "../lib/curve";
import type { Lang, VerdictResult } from "../lib/types";
import AngerCurveViz from "./AngerCurveViz";
import TypeArt from "./TypeArt";
import HyuhPromo from "./HyuhPromo";

interface Props {
  t: Strings;
  lang: Lang;
  result: VerdictResult;
  onShare: () => void;
  onBack: () => void;
}

// ⑦ 상세 리포트 (광고 후): 곡선 + 스탯 4행 + AI 스탯 코멘트.
export default function Detail({ t, lang, result, onShare, onBack }: Props) {
  const { judgement, curve, archetype, signals } = result;
  const color = archetypeColor(archetype);
  const name = ARCHETYPE_NAME[archetype][lang];
  const stats = deriveDisplayStats(signals);
  const peakPct = Math.round((curve.peakIndex / (BUCKET_COUNT - 1)) * 100);
  const shown = useCountUp(result.score);

  const rows: [string, string][] = [
    [t.stSpeed, `${stats.speed}/min`],
    [t.stBack, `${stats.backspaces}`],
    [t.stTime, `${stats.timeSpentSec}s`],
    [t.stFirst, `${stats.toFirstSec}s`],
  ];

  return (
    <div className="screen detail">
      <div className="rep-header">
        <button className="navbtn" onClick={onBack}>
          ← {t.back}
        </button>
      </div>

      <div className="detail-meta">
        <span className="rep-meta">BWEK · {locDate(lang)}</span>
        <span className="rep-meta" style={{ color }}>
          {name}
        </span>
      </div>

      {/* 고정 요약 카드: 점수 + 유형 + AI 멘트 (⑤에서 이월) */}
      <div className="pinned-summary">
        <TypeArt typeId={archetype} accent={color} ink="#fff" size={56} sw={3.4} draw />
        <div className="pinned-body">
          <div className="pinned-row">
            <span className="pinned-score" style={{ color }}>
              {shown}
            </span>
            <span className="pinned-suffix">/100</span>
            <span className="pinned-name">{name}</span>
          </div>
          <div className="pinned-verdict">“{judgement.headline}”</div>
        </div>
      </div>

      <div className="detail-curve">
        <AngerCurveViz curve={curve} color={color} w={320} h={120} hatch showPeak showAxis animate />
        <div className="curve-caption">
          <span>{t.angerCurve}</span>
          <span>
            {t.peak} · {peakPct}%
          </span>
        </div>
      </div>

      <div className="stat-rows">
        {rows.map(([label, val]) => (
          <div className="stat-row" key={label}>
            <span className="stat-label">{label}</span>
            <span className="stat-val">{val}</span>
          </div>
        ))}
      </div>

      <div className="ai-stat">
        <span className="ai-label" style={{ color }}>
          {t.aiStat}
        </span>
        {judgement.report_notes.map((note, i) => (
          <p key={i} className="ai-stat-line">
            {note}
          </p>
        ))}
      </div>

      {/* hyuh 크로스프로모 — AI 코멘트 바로 아래. 토스·미승인 iOS에선 자동 숨김. */}
      <HyuhPromo t={t} place="result" />

      <button className="cta-solid" style={{ background: color }} onClick={onShare}>
        {t.flexit}
      </button>
    </div>
  );
}
